import { useState, useMemo } from "react";
import { Calculator } from "lucide-react";
import { Slider } from "@/components/ui/slider";

export default function EMICalculator() {
  const [cost, setCost] = useState(2000000);
  const [booking, setBooking] = useState(400000);
  const [rate, setRate] = useState(9);
  const [years, setYears] = useState(10);

  const { emi, loan, total, interest } = useMemo(() => {
    const loan = Math.max(cost - booking, 0);
    const r = rate / 12 / 100;
    const n = years * 12;
    const emi = r === 0 ? loan / n : (loan * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const total = emi * n;
    return { emi: Math.round(emi), loan, total: Math.round(total), interest: Math.round(total - loan) };
  }, [cost, booking, rate, years]);

  const fmt = (n) => "₹" + n.toLocaleString("en-IN");

  const Row = ({ label, value, onChange, min, max, step, display }) => (
    <div>
      <div className="flex justify-between mb-2"><span className="text-sm font-medium text-gray-700">{label}</span><span className="text-sm font-bold text-[#0B5D3B]">{display}</span></div>
      <Slider value={[value]} min={min} max={max} step={step} onValueChange={(v)=>onChange(v[0])} />
    </div>
  );

  return (
    <div className="font-body">
      <div className="bg-[#08341f] py-16 text-center text-white">
        <p className="text-sm uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">EMI Calculator</p>
        <h1 className="font-heading text-4xl lg:text-5xl font-bold mt-3">Plan Your Investment</h1>
      </div>
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-14 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="rounded-2xl border border-gray-100 p-8 space-y-7" data-testid="emi-inputs">
          <Row label="Plot Cost" value={cost} onChange={setCost} min={500000} max={10000000} step={50000} display={fmt(cost)} />
          <Row label="Booking Amount" value={booking} onChange={setBooking} min={0} max={cost} step={25000} display={fmt(booking)} />
          <Row label="Interest Rate (p.a.)" value={rate} onChange={setRate} min={6} max={15} step={0.25} display={`${rate}%`} />
          <Row label="Loan Tenure" value={years} onChange={setYears} min={1} max={25} step={1} display={`${years} yrs`} />
        </div>
        <div className="rounded-2xl bg-[#0B5D3B] text-white p-8 flex flex-col justify-center" data-testid="emi-result">
          <Calculator size={36} className="text-[#D4AF37]" />
          <p className="text-sm text-white/70 mt-6">Your Monthly EMI</p>
          <p className="font-heading text-5xl font-bold mt-1">{fmt(emi)}</p>
          <div className="mt-8 space-y-3 border-t border-white/15 pt-6 text-sm">
            <div className="flex justify-between"><span className="text-white/70">Loan Amount</span><span className="font-semibold">{fmt(loan)}</span></div>
            <div className="flex justify-between"><span className="text-white/70">Total Interest</span><span className="font-semibold">{fmt(interest)}</span></div>
            <div className="flex justify-between"><span className="text-white/70">Total Payable</span><span className="font-semibold">{fmt(total)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
